import { RoutingItem } from '../../../App/model/routing-item';
import { MxAction } from '../../Pandora/model/action/action';

/**
 * @ngdoc type
 * @name NavigationItem
 * @module mx.sb.Navigation
 *
 * @description
 * An object containing navigation item information (Navigation item model)
 * Constructor accepts title, name, routingItem as params like:
 *
 * ```javascript
 * var navigationItem = new mx.shell.NavigationItem(title, name, routingItem);
 * ```
 *
 * @property {string} title - Gets or sets Display name of the Navigation item
 * @property {string} name - Gets or sets Unique name of the Navigation item
 * @property {string | RoutingItem} routingItem - Gets or sets {@ref RoutingItem routing item} of the navigation item
 * @property {string} icon - Gets or sets Icon associated with Navigation item
 * @property {mx.shell.Action[]} actions - Gets or sets an array of actions to be displayed in navigation item
 * @property {NavigationItem[]} children - Gets or sets an array of navigation item models that should be displayed under current one
 * @property {object} parameters - Gets or sets object that contains parameters for routing
 * @property {boolean} disabled - Gets or sets flag that defines navigation item as a disabled
 * @property {boolean} openInNewWindow - Gets or sets flag that defines navigation item must open URL in new window or not
 * @property {string} route - Gets route string
 * @property {function} childrenDynamicLoader - if set then this item support dynamic loading for children items.
 *                Expects a method that gets just clicked item as incoming parameter
 *                and returns a promise result of which must be an array of `mx.shell.NavigationItem`s.
 * @property {string} defaultFilter - Gets or sets an id of a default filter for this navigation item
 * @property {boolean} configurable - Gets or sets a flag that defines if navigation item is configurable
 * @property {boolean} configurationModeOn - Gets or sets a flag that defines the navigation item is or is not in configuration mode
 * @property {boolean} beingConfigured - Gets or sets a flag that defines the navigation item is being configured now
 * @property {mx.shell.Action[]} configurationActions - Gets or sets an array of actions to be displayed in navigation item when it is in a configuration mode
 * @property {boolean} draggable - Gets or sets a flag that defines if navigation item is draggable
 *
 * ```javascript
 *    var myRootNavItem = new mx.shell.NavigationItem('My Root Nav Item', myRootNavItem);
 *    myRootNavItem.childrenDynamicLoader = function (clickedItem) {
 * 		return $http.get('get/clickedItem/children.json').then(function (itemsFromServer) {
 * 			return itemsFromServer.map(function () {
 *				return  new mx.shell.NavigationItem(item.title, newItemName, item.routingItem);
 *			});
 *		});
 *	}
 * ```
 */

export type NavigationItemParameters = {
	navigationId: string;
	filterId?: string;
	filterType?: 'nofilter' | 'filter' | 'structure';
	appString: string;
};

export class NavigationItem {
	public parameters?: NavigationItemParameters;
	public id?: string;
	public icon?: string;
	public dataQuery?: string;
	public application?: string;
	public actions: MxAction[] = [];
	public configurationActions: MxAction[] = [];
	public children?: NavigationItem[];
	public parent?: NavigationItem;
	public disabled = false;
	public openInNewWindow = false;
	public configurable = false;
	public beingConfigured = false;
	public draggable = false;
	public availableInSearch = false;
	public hideLeftSideNavigation = false;
	public matchesFilterCriteria?: boolean;
	public defaultFilter?: string;
	public contentWidgetId?: string;
	public quickFilters?: [string, string, string];
	public childrenDynamicLoader?: (item: NavigationItem) => Promise<NavigationItem[]>;

	public get route() {
		return !this.routingItem ? null : typeof this.routingItem === 'object' ? this.routingItem.name : this.routingItem;
	}

	private _configurationModeOn = false;
	public get configurationModeOn() {
		return this._configurationModeOn;
	}
	public set configurationModeOn(newValue) {
		this._configurationModeOn = newValue;
		if (Array.isArray(this.children)) {
			this.children.forEach((item) => (item.configurationModeOn = newValue));
		}
	}

	constructor(public title: string, public name: string, public technicalName: string, public routingItem?: RoutingItem) {}
}
